import {
  ComponentType,
  Dispatch,
  ReactElement,
  SetStateAction,
  SVGProps,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useForm, useStore } from '@tanstack/react-form';
import { useNavigate, useSearch } from '@tanstack/react-router';
import {
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  Cog6ToothIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  GlobeAltIcon,
  InformationCircleIcon,
  PencilSquareIcon,
  PlusIcon,
  XCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import GlobeIcon from '../assets/icons/globe-01.svg';
import StopwatchIcon from '../assets/icons/stopwatch.svg';
import {
  useApolloClient,
  useMutation,
  useSuspenseQuery,
} from '@apollo/client/react';
import {
  GQL_FRAGMENT_THEME,
  GQL_MUTATION_UPDATE_PROJECT,
  GQL_MUTATION_UPDATE_RATE_LIMIT,
  GQL_QUERY_INDEX_DOMAINS,
  GQL_QUERY_INDEX_THEMES,
  GQL_QUERY_READ_PROJECT_RULES,
  GQL_QUERY_READ_RATE_LIMIT,
} from '../utility/queries';
import {
  Alert,
  Button,
  ComboxBox,
  IconButton,
  Input,
  ListBox,
  Modal,
  ModalFooter,
  NumberInput,
  RadioGroup,
} from '@sc/shared-ui';
import {
  getFragmentData,
  ProjectFragment,
  ProjectRuleType,
  ProjectState,
  projectStateDisplayMap,
  SubscriptionType,
  UserCompanyLimitation,
} from '@sc/graphql';
import { notify } from '../utility/notify';
import { track } from '../utility/analytics';
import classNames from 'classnames';
import { useUser } from '../providers/users';
import { useAlerts } from '../providers/alerts';
import env from '../environment';
import { BillingLite } from '../components/UpgradeLite';
import { maxQueryMultiplierBySubscription } from '@sc/stripe/pricing';
import { getProjectUrl } from '../utility/projectUrl';
import { CreateDomainModal } from './CreateDomainModal';
import {
  DomainVerificationModal,
  VerifiableDomain,
} from './DomainVerificationModal';

interface Props {
  open: boolean;
  onClose: () => void;
  project: ProjectFragment;
  onPublished?: (() => void) | undefined;
}

const validateQueryMultiplier = (
  value: string,
  maxQueryMultiplier: number
): string | undefined => {
  const val = parseInt(value);

  if (val > maxQueryMultiplier) {
    return `Based on your subscription, you cannot have a query depth above ${maxQueryMultiplier}x.`;
  }

  if (val < 1) {
    return 'A minimum of 1x is required.';
  }

  return undefined;
};

type SettingsTabType =
  | 'domain'
  | 'limits'
  | 'questions'
  | 'settings'
  | 'visibility';

const SETTINGS_TABS: SettingsTabType[] = [
  'domain',
  'limits',
  'questions',
  'settings',
  'visibility',
];

// Deep-link support: ?modal=settings-<tab> opens the modal on that tab
// (e.g. settings-limits, settings-domain). settings-publish is the guided
// publish flow, not a tab.
const tabForModalParam = (
  modal: string | undefined
): SettingsTabType | undefined => {
  const requested = modal?.replace(/^settings-/, '');

  return SETTINGS_TABS.find((t) => t === requested);
};

const PUBLISH_SUBSCRIPTIONS = [
  SubscriptionType.essentials,
  SubscriptionType.enterprise,
];

const PUBLISH_UPGRADE_PROMPT =
  'Upgrading will allow you to publish and share your knowledge base';

const ProjectSettingsInner = ({
  onClose,
  onPublished,
  project,
  setLoading,
}: Omit<Props, 'open'> & { setLoading: Dispatch<SetStateAction<boolean>> }) => {
  const { useApolloError } = useAlerts();

  const onError = useApolloError();

  const client = useApolloClient();

  const { user } = useUser();

  const { modal } = useSearch({ strict: false }) as unknown as {
    modal?: string;
  };

  const initialTab = tabForModalParam(modal);

  const isPublishMode = modal === 'settings-publish';

  const { data: rules } = useSuspenseQuery(GQL_QUERY_READ_PROJECT_RULES, {
    variables: {
      uuid: project.uuid,
    },
    context: {
      isBatched: true,
    },
  });

  const hasPrivacyConfig = rules.readProject.rules.some(
    (r) => r.type === ProjectRuleType.visibility
  );

  const [tab, setTab] = useState<SettingsTabType>(
    isPublishMode && !hasPrivacyConfig ? 'visibility' : initialTab ?? 'settings'
  );

  // Follow the URL if the modal param changes while the modal is mounted.
  useEffect(() => {
    if (initialTab) setTab(initialTab);
  }, [initialTab]);

  useEffect(() => {
    if (isPublishMode)
      track('Project Publish Modal Viewed', { projectUuid: project.uuid });
  }, [isPublishMode, project.uuid]);

  const navigate = useNavigate();

  // Tab changes are reflected in ?modal=settings-<tab> so the current tab is
  // linkable. The guided publish flow keeps its own param while browsing.
  const selectTab = (next: SettingsTabType) => {
    setTab(next);

    if (!isPublishMode) {
      void navigate({
        to: '.',
        search: (prev) => ({ ...prev, modal: `settings-${next}` }),
        replace: true,
      });
    }
  };

  const { data: rateLimit } = useSuspenseQuery(GQL_QUERY_READ_RATE_LIMIT, {
    variables: {
      projectUuid: project.uuid,
    },
  });

  const { data: themeData } = useSuspenseQuery(GQL_QUERY_INDEX_THEMES, {
    variables: { input: { pagination: { page: 1, perPage: 50 } } },
  });

  const { data: domains } = useSuspenseQuery(GQL_QUERY_INDEX_DOMAINS, {
    variables: {
      input: {
        pagination: {
          perPage: 15,
          page: 1,
        },
        filters: {
          unallocated: true,
        },
      },
    },
    context: {
      isBatched: true,
    },
  });

  const themes = themeData.indexThemes.items.map((i) =>
    getFragmentData(GQL_FRAGMENT_THEME, i)
  );

  const [updateProject, { loading: isSavingProject }] = useMutation(
    GQL_MUTATION_UPDATE_PROJECT
  );

  const [updateRateLimit, { loading: isSavingRateLimit }] = useMutation(
    GQL_MUTATION_UPDATE_RATE_LIMIT
  );

  const isSaving = isSavingProject || isSavingRateLimit;

  useEffect(() => setLoading(isSaving), [isSaving, setLoading]);

  const maxQueryMultiplier =
    maxQueryMultiplierBySubscription[
      user.company.subscription?.type ?? SubscriptionType.essentials
    ];

  const canSendQueryMessages = !user.company.limitations.includes(
    UserCompanyLimitation.cannotCreateQueryMessage
  );

  const canPublish =
    project.sourceCount > 0 &&
    project.processedPc === 100 &&
    canSendQueryMessages;

  const canPublishBySubscription =
    user.company.subscription?.type &&
    PUBLISH_SUBSCRIPTIONS.includes(user.company.subscription.type);

  const [showUpgrade, setShowUpgrade] = useState(false);

  const forcePublishRef = useRef(false);

  // Richer queries are an advanced opt-in; surface the section only when the
  // project already uses a multiplier above the standard 1x.
  const [showAdvancedLimits, setShowAdvancedLimits] = useState(
    rateLimit.readRateLimit.queryMultiplier > 1
  );

  const form = useForm({
    defaultValues: {
      name: project.name,
      title: project.title ?? '',
      subtitle: project.subtitle ?? '',
      questions: project.questions,
      themeName: project.theme?.name ?? 'Default',
      state:
        isPublishMode && canPublish ? ProjectState.published : project.state,
      visibility:
        (rules.readProject.rules.find(
          (r) => r.type === ProjectRuleType.visibility
        )?.value as 'magic-link' | 'public' | undefined) ?? 'magic-link',
      rules: rules.readProject.rules,
      domains: project.domains.map((d) => ({ label: d.url, value: d.uuid })),
      queryMultiplier: String(rateLimit.readRateLimit.queryMultiplier),
      queriesPerUserPerHour: String(
        rateLimit.readRateLimit.queriesPerUserPerHour
      ),
    },
    onSubmitInvalid: ({ formApi }) => {
      const fieldToTab: Record<string, SettingsTabType> = {
        name: 'settings',
        title: 'settings',
        subtitle: 'settings',
        themeName: 'settings',
        state: 'settings',
        queryMultiplier: 'limits',
        queriesPerUserPerHour: 'limits',
        visibility: 'visibility',
        rules: 'visibility',
        domains: 'domain',
        questions: 'questions',
      };

      const firstInvalid = Object.keys(fieldToTab).find(
        (field) =>
          formApi.getFieldMeta(field as keyof typeof formApi.state.values)
            ?.errors.length
      );

      const invalidTab = firstInvalid ? fieldToTab[firstInvalid] : undefined;

      if (invalidTab) selectTab(invalidTab);
    },
    onSubmit: async ({ value }) => {
      if (
        value.state === ProjectState.published &&
        project.state !== ProjectState.published
      )
        track('Project Publish Submitted', { projectUuid: project.uuid });

      const isGatedPublish =
        value.state === ProjectState.published &&
        !canPublishBySubscription &&
        !forcePublishRef.current;

      forcePublishRef.current = false;

      const projectRules =
        value.visibility === 'public'
          ? [
              {
                type: ProjectRuleType.visibility,
                value: value.visibility,
              },
            ]
          : [
              {
                type: ProjectRuleType.visibility,
                value: value.visibility,
              },
              ...value.rules
                .filter((f) => f.type !== ProjectRuleType.visibility)
                .map(({ value, type }) => ({ value, type })),
            ];

      try {
        await updateProject({
          variables: {
            input: {
              uuid: project.uuid,
              name: value.name,
              title: value.title.trim() || null,
              subtitle: value.subtitle.trim() || null,
              questions: value.questions.map(({ question }) => ({ question })),
              state: isGatedPublish ? ProjectState.draft : value.state,
              themeUuid:
                themes.find((t) => t.name === value.themeName)?.uuid ?? null,
              rules: projectRules,
              customDomainUuids: value.domains.map((d) => d.value),
            },
          },
          refetchQueries: [
            GQL_QUERY_INDEX_DOMAINS,
            GQL_QUERY_READ_PROJECT_RULES,
          ],
        });
      } catch (error) {
        onError(error);

        return;
      }

      track('Project Updated', {
        projectUuid: project.uuid,
        visibility: value.visibility,
        domainCount: value.domains.length,
      });

      if (value.domains.length !== project.domains.length)
        track('Domain Applied To Project', {
          projectUuid: project.uuid,
          domainCount: value.domains.length,
        });

      if (value.themeName !== (project.theme?.name ?? 'Default'))
        track('Theme Applied To Project', {
          projectUuid: project.uuid,
          source: 'settings',
        });

      const originalQuestionsByUuid = new Map(
        project.questions.map((q) => [q.uuid, q.question])
      );

      const stagedQuestionUuids = new Set(value.questions.map((q) => q.uuid));

      const questionsCreated = value.questions.filter(
        (q) => !originalQuestionsByUuid.has(q.uuid)
      ).length;

      const questionsDeleted = project.questions.filter(
        (q) => !stagedQuestionUuids.has(q.uuid)
      ).length;

      if (questionsCreated || questionsDeleted)
        track('Project Questions Updated', {
          projectUuid: project.uuid,
          questionCount: value.questions.length,
          questionsCreated,
          questionsDeleted,
        });

      const hasRateLimitChanged =
        parseInt(value.queryMultiplier) !==
          rateLimit.readRateLimit.queryMultiplier ||
        parseInt(value.queriesPerUserPerHour) !==
          rateLimit.readRateLimit.queriesPerUserPerHour;

      if (hasRateLimitChanged) {
        const newQueryMultiplier = parseInt(value.queryMultiplier);

        const newQueriesPerHour = parseInt(value.queriesPerUserPerHour);

        try {
          await updateRateLimit({
            variables: {
              input: {
                projectUuid: project.uuid,
                queryMultiplier: newQueryMultiplier,
                queriesPerUserPerHour: newQueriesPerHour,
              },
            },
          });
        } catch {
          notify.error(
            'Knowledge base settings saved, but rate limits failed to update. Please try again.'
          );

          selectTab('limits');

          return;
        }

        track('Rate Limit Updated', {
          projectUuid: project.uuid,
          queryMultiplier: newQueryMultiplier,
          queriesPerUserPerHour: newQueriesPerHour,
        });
      }

      if (isGatedPublish) {
        track('Project Publish Blocked By Plan', {
          projectUuid: project.uuid,
        });

        setShowUpgrade(true);

        return;
      }

      const isNewlyPublished =
        value.state === ProjectState.published &&
        project.state !== ProjectState.published;

      if (isNewlyPublished)
        track('Project Published', { projectUuid: project.uuid });

      if (isNewlyPublished && onPublished) {
        onPublished();

        return;
      }

      notify.success('Successfully saved knowledge base settings.');

      onClose();
    },
  });

  const stagedState = useStore(form.store, (v) => v.values.state);

  const visibilityRule = useStore(form.store, (v) => v.values.visibility);

  const stagedRules = useStore(form.store, (v) => v.values.rules);

  const emailRuleCount = stagedRules.filter(
    (r) => r.type === ProjectRuleType.email
  ).length;

  const domainRuleCount = stagedRules.filter(
    (r) => r.type === ProjectRuleType.domain
  ).length;

  const domainName = getProjectUrl(project, env.chatsUrl);

  const canAddDomain = [
    SubscriptionType.essentials,
    SubscriptionType.enterprise,
  ].includes(user.company.subscription?.type as SubscriptionType);

  const [showDomainUpgrade, setShowDomainUpgrade] = useState(false);

  const [showCreateDomain, setShowCreateDomain] = useState(false);

  const [verifyDomain, setVerifyDomain] = useState<
    VerifiableDomain | undefined
  >(undefined);

  const [showConfigRules, setShowConfigRules] = useState<
    ProjectRuleType | undefined
  >(undefined);

  // Switching rule type keeps the modal open, so key on open/closed.
  const isRulesModalOpen = !!showConfigRules;

  useEffect(() => {
    if (isRulesModalOpen)
      track('Project Rules Modal Viewed', { projectUuid: project.uuid });
  }, [isRulesModalOpen, project.uuid]);

  const ruleForm = useForm({
    defaultValues: {
      value: '',
    },
    onSubmit: ({ value, formApi }) => {
      if (!showConfigRules) return;

      form.setFieldValue('rules', (prev) => [
        ...prev,
        {
          uuid: String(Math.random()),
          type: showConfigRules,
          value: value.value.trim(),
        },
      ]);

      track('Project Rule Added', {
        projectUuid: project.uuid,
        ruleType: showConfigRules,
      });

      formApi.reset();
    },
  });

  useEffect(() => {
    ruleForm.reset();
  }, [ruleForm, showConfigRules]);

  const stagedQuestions = useStore(form.store, (v) => v.values.questions);

  const questionForm = useForm({
    defaultValues: {
      value: '',
    },
    onSubmit: ({ value, formApi }) => {
      form.setFieldValue('questions', (prev) => [
        ...prev,
        {
          uuid: String(Math.random()),
          question: value.value.trim(),
        },
      ]);

      formApi.reset();
    },
  });

  const tabs: Array<{
    value: SettingsTabType;
    label: string;
    Icon: ComponentType<SVGProps<SVGSVGElement>>;
  }> = [
    { value: 'settings', label: 'Settings', Icon: PencilSquareIcon },
    { value: 'questions', label: 'Questions', Icon: ChatBubbleLeftRightIcon },
    { value: 'limits', label: 'Rate limits', Icon: StopwatchIcon },
    { value: 'visibility', label: 'Visibility', Icon: EyeIcon },
    { value: 'domain', label: 'Domain', Icon: GlobeAltIcon },
  ];

  return (
    <>
      <form
        onSubmit={(e) => {
          e.preventDefault();

          void form.handleSubmit();
        }}
        className="flex flex-col overflow-hidden grow"
      >
        <div className="flex overflow-hidden grow">
          <ul className="p-2 border-r border-grey-t-800 space-y-2">
            {tabs.map(({ value, label, Icon }) => (
              <li key={value} className="flex w-full">
                <button
                  type="button"
                  data-selected={tab === value ? true : undefined}
                  className="p-3 grow data-selected:bg-primary-400/10 flex items-center space-x-2 hover:bg-primary-400/10 rounded-xs"
                  onClick={() => selectTab(value)}
                >
                  <Icon className="size-5" />
                  <div className="w-[180px] text-start grow">
                    <span className="grow">{label}</span>
                  </div>
                </button>
              </li>
            ))}
          </ul>
          <div className="w-[600px] flex flex-col grow overflow-y-auto">
            <div
              data-hidden={tab !== 'settings' ? true : undefined}
              className="data-hidden:hidden p-5 space-y-5"
            >
              <form.Field
                name="name"
                validators={{
                  onSubmit: ({ value }) =>
                    value.trim().length ? undefined : 'Please enter a name.',
                }}
                children={({ state, handleChange }) => (
                  <Input
                    label="Knowledge base name"
                    required
                    value={state.value}
                    error={state.meta.errors.join(', ')}
                    onChange={(e) => handleChange(e.target.value)}
                  />
                )}
              />
              <form.Field
                name="themeName"
                children={({ state, handleChange }) => (
                  <ListBox
                    label="Brand theme"
                    options={[
                      { label: 'Default', value: 'Default' },
                      ...themes.map((t) => ({ label: t.name, value: t.name })),
                    ]}
                    selected={state.value}
                    setSelected={(name) => handleChange(name)}
                  />
                )}
              />
              <form.Field
                name="state"
                children={({ state, handleChange }) => (
                  <div className="space-y-2">
                    <ListBox
                      label="Status"
                      options={[
                        {
                          label: projectStateDisplayMap[ProjectState.draft],
                          value: projectStateDisplayMap[ProjectState.draft],
                        },
                        {
                          label: projectStateDisplayMap[ProjectState.published],
                          value: projectStateDisplayMap[ProjectState.published],
                        },
                      ]}
                      selected={projectStateDisplayMap[state.value]}
                      setSelected={(label) =>
                        handleChange(
                          label ===
                            projectStateDisplayMap[ProjectState.published]
                            ? ProjectState.published
                            : ProjectState.draft
                        )
                      }
                      disabled={
                        !canPublish && state.value === ProjectState.draft
                      }
                    />
                    {!canPublish && state.value === ProjectState.draft && (
                      <span className="text-text-low-priority text-sm block">
                        {project.sourceCount === 0
                          ? 'Add at least one source before publishing.'
                          : project.processedPc < 100
                          ? 'Your sources are still processing.'
                          : 'No usage remaining - upgrade your plan before publishing.'}
                      </span>
                    )}
                  </div>
                )}
              />
              <form.Field
                name="title"
                children={({ state, handleChange }) => (
                  <Input
                    label="Chat title"
                    helperText="Shown above the chat input. Overrides the theme's title when set"
                    value={state.value}
                    error={state.meta.errors.join(', ')}
                    onChange={(e) => handleChange(e.target.value)}
                  />
                )}
              />
              <form.Field
                name="subtitle"
                children={({ state, handleChange }) => (
                  <Input
                    label="Chat subtitle"
                    helperText="Shown beneath the title. Overrides the theme's subtitle when set"
                    value={state.value}
                    error={state.meta.errors.join(', ')}
                    onChange={(e) => handleChange(e.target.value)}
                  />
                )}
              />
            </div>
            <div
              data-hidden={tab !== 'questions' ? true : undefined}
              className="data-hidden:hidden p-5 space-y-5"
            >
              <form.Field
                name="questions"
                children={({ state, handleChange }) => (
                  <>
                    <div
                      onKeyDown={(e) => {
                        // Enter adds a question instead of submitting the modal.
                        if (e.key === 'Enter') {
                          e.preventDefault();

                          void questionForm.handleSubmit();
                        }
                      }}
                    >
                      <questionForm.Field
                        name="value"
                        validators={{
                          onSubmit: ({ value }) => {
                            const trimmed = value.trim();

                            if (!trimmed) return 'Please enter a question.';

                            return stagedQuestions.some(
                              (q) =>
                                q.question.toLowerCase() ===
                                trimmed.toLowerCase()
                            )
                              ? 'This question has already been added.'
                              : undefined;
                          },
                        }}
                        children={({
                          state: questionState,
                          handleChange: handleQuestionChange,
                        }) => (
                          <Input
                            value={questionState.value}
                            error={questionState.meta.errors.join(', ')}
                            label="Suggested questions"
                            helperText="Shown as “Questions you could ask” beneath the chat input — three show at a time and rotate every few seconds. Generated automatically from your sources when none exist."
                            onChange={(e) =>
                              handleQuestionChange(e.target.value)
                            }
                            inlineButton={{
                              button: {
                                type: 'secondary',
                                text: 'Add',
                                IconEnd: PlusIcon,
                                shrink: true,
                                onClick: () => void questionForm.handleSubmit(),
                              },
                            }}
                          />
                        )}
                      />
                    </div>
                    <div className="space-y-2">
                      {state.value.map((q) => (
                        <div
                          key={q.uuid}
                          className="flex items-center space-x-1 bg-grey-t-900 justify-between border border-grey-500 rounded-xs"
                        >
                          <span className="pl-2.5 py-2 text-sm">
                            {q.question}
                          </span>
                          <IconButton
                            onClick={() =>
                              handleChange((vals) =>
                                vals.filter((val) => val.uuid !== q.uuid)
                              )
                            }
                            Icon={XMarkIcon}
                          />
                        </div>
                      ))}
                    </div>
                  </>
                )}
              />
            </div>
            <div
              data-hidden={tab !== 'limits' ? true : undefined}
              className="data-hidden:hidden p-5 space-y-5"
            >
              <form.Field
                name="queriesPerUserPerHour"
                children={({ state, handleChange }) => (
                  <NumberInput
                    value={state.value}
                    onChange={(e) => handleChange(e.target.value)}
                    label="Queries per user per hour"
                    helperText="This is the maximum queries a user can create in an hour."
                  />
                )}
              />
              <button
                type="button"
                onClick={() => setShowAdvancedLimits((open) => !open)}
                className="flex items-center justify-between gap-3 w-full text-left rounded-xs px-3 py-2 hover:bg-grey-t-900 active:shadow-focused-dark transition-colors"
              >
                <span className="font-semibold text-body-medium">Advanced</span>
                <ChevronDownIcon
                  className={classNames(
                    'size-5 text-white shrink-0 transition-transform',
                    showAdvancedLimits && 'rotate-180'
                  )}
                  aria-hidden="true"
                />
              </button>
              <div
                data-hidden={!showAdvancedLimits ? true : undefined}
                className="data-hidden:hidden"
              >
                <form.Field
                  name="queryMultiplier"
                  validators={{
                    onChange: ({ value }) =>
                      validateQueryMultiplier(value, maxQueryMultiplier),
                    onSubmit: ({ value }) =>
                      validateQueryMultiplier(value, maxQueryMultiplier),
                  }}
                  children={({ state, handleChange, handleBlur }) => (
                    <>
                      <NumberInput
                        value={state.value}
                        onChange={(e) => handleChange(e.target.value)}
                        onBlur={handleBlur}
                        required
                        error={state.meta.errors.join(', ')}
                        label="Query depth multiplier"
                        helperText={
                          <>
                            <ExclamationTriangleIcon
                              className="inline size-4 text-amber-400 mr-1.5 align-text-bottom"
                              aria-hidden="true"
                            />
                            Query depth allows your users to have longer
                            conversations, your allowance will be consumed
                            faster the higher the depth.
                          </>
                        }
                      />
                      {!state.meta.errors.length && (
                        <span className="text-xs block text-text-low-priority mt-2">
                          1 query costs {state.value} quer
                          {parseInt(state.value) > 1 ? 'ies' : 'y'}
                        </span>
                      )}
                    </>
                  )}
                />
              </div>
            </div>
            <div
              data-hidden={tab !== 'visibility' ? true : undefined}
              className="data-hidden:hidden p-5 space-y-5"
            >
              <form.Field
                name="visibility"
                children={({ state, handleChange }) => (
                  <div className="space-y-3">
                    <RadioGroup<'magic-link' | 'public'>
                      error={state.meta.errors.join(', ')}
                      label="Who can access to the chat?"
                      options={[
                        {
                          label: 'Anyone with the link',
                          value: 'public',
                        },
                        {
                          label:
                            'Specify who can access with rules (Recommended)',
                          value: 'magic-link',
                        },
                      ]}
                      setSelected={(opt) => {
                        handleChange(opt);

                        if (opt === 'public') form.setFieldValue('rules', []);
                      }}
                      selected={state.value}
                    />
                    {state.value === 'public' && (
                      <Alert
                        type="warning"
                        size="small"
                        message="Anyone with the link will be able to access this chat."
                      />
                    )}
                  </div>
                )}
              />

              <form.Field
                name="rules"
                validators={{
                  onSubmit: (v) =>
                    stagedState !== ProjectState.published ||
                    visibilityRule !== 'magic-link' ||
                    v.value.filter((v) => v.type !== ProjectRuleType.visibility)
                      .length
                      ? undefined
                      : 'Please add a set of rules.',
                }}
                children={({ state, handleChange }) => (
                  <div
                    data-hidden={
                      visibilityRule !== 'magic-link' ? true : undefined
                    }
                    className="data-hidden:hidden block"
                  >
                    <span className="text-body-medium font-semibold block">
                      Rules
                    </span>
                    <span className="mb-2 mt-1 text-text-low-priority text-sm block">
                      Rules govern who has access to your knowledge base.
                    </span>
                    {emailRuleCount || domainRuleCount ? (
                      <ul className="mb-3 space-y-1">
                        {!!emailRuleCount && (
                          <li className="flex items-center space-x-1.5 text-sm">
                            <CheckCircleIcon className="size-4 text-primary-400 shrink-0" />
                            <span>
                              {emailRuleCount} email{' '}
                              {emailRuleCount === 1 ? 'rule' : 'rules'}
                            </span>
                          </li>
                        )}
                        {!!domainRuleCount && (
                          <li className="flex items-center space-x-1.5 text-sm">
                            <CheckCircleIcon className="size-4 text-primary-400 shrink-0" />
                            <span>
                              {domainRuleCount} domain{' '}
                              {domainRuleCount === 1 ? 'rule' : 'rules'}
                            </span>
                          </li>
                        )}
                      </ul>
                    ) : (
                      <div className="flex items-center space-x-1.5 mb-3">
                        <XCircleIcon className="size-4 text-red-400 shrink-0" />
                        <span className="text-sm text-text-low-priority">
                          No rules added yet
                        </span>
                      </div>
                    )}

                    <Button
                      button={{
                        text: 'Configure rules',
                        type: 'low-priority',
                        size: 'small',
                        shrink: true,
                        onClick: () =>
                          setShowConfigRules(ProjectRuleType.email),
                      }}
                    />
                    {!!state.meta.errors.length && (
                      <div className="flex space-x-1.5 items-start mt-2">
                        <InformationCircleIcon className="shrink-0 size-4 text-red-300 mt-[3px]" />
                        <span className="text-body-smallest text-red-300">
                          {state.meta.errors.join(', ')}
                        </span>
                      </div>
                    )}
                    <Modal
                      open={!!showConfigRules}
                      onClose={() => setShowConfigRules(undefined)}
                      title="Configure rules"
                    >
                      <div className="flex">
                        <ul className="p-5 border-r border-grey-t-800 space-y-2">
                          <li className="flex">
                            <button
                              type="button"
                              data-selected={
                                showConfigRules === ProjectRuleType.email
                                  ? true
                                  : undefined
                              }
                              className="p-3 data-selected:bg-grey-t-800 flex items-center space-x-2 hover:bg-grey-t-800 rounded-xs"
                              onClick={() =>
                                setShowConfigRules(ProjectRuleType.email)
                              }
                            >
                              <EnvelopeIcon className="size-5" />
                              <div className="w-45 text-start">
                                <span className="grow">Emails</span>
                              </div>
                              <div
                                className={classNames(
                                  'px-2 py-1 rounded-full flex',
                                  {
                                    'bg-[#70808E] text-text-filled-component':
                                      state.value.filter(
                                        (v) => v.type === ProjectRuleType.email
                                      ).length,
                                  }
                                )}
                              >
                                <span className="text-xs leading-4 font-bold">
                                  {
                                    state.value.filter(
                                      (v) => v.type === ProjectRuleType.email
                                    ).length
                                  }
                                </span>
                              </div>
                            </button>
                          </li>
                          <li className="flex">
                            <button
                              type="button"
                              data-selected={
                                showConfigRules === ProjectRuleType.domain
                                  ? true
                                  : undefined
                              }
                              className="p-3 data-selected:bg-grey-t-800 flex items-center space-x-2 hover:bg-grey-t-800 rounded-xs"
                              onClick={() =>
                                setShowConfigRules(ProjectRuleType.domain)
                              }
                            >
                              <GlobeIcon className="size-5" />
                              <div className="w-45 text-start">
                                <span className="grow">Domains</span>
                              </div>
                              <div
                                className={classNames(
                                  'px-2 py-1 rounded-full flex',
                                  {
                                    'bg-[#70808E] text-text-filled-component':
                                      state.value.filter(
                                        (v) => v.type === ProjectRuleType.domain
                                      ).length,
                                  }
                                )}
                              >
                                <span className="text-xs leading-4 font-bold">
                                  {
                                    state.value.filter(
                                      (v) => v.type === ProjectRuleType.domain
                                    ).length
                                  }
                                </span>
                              </div>
                            </button>
                          </li>
                        </ul>
                        <div className="w-125 flex flex-col h-120">
                          <div className="p-5">
                            <form
                              className="flex w-full"
                              onSubmit={(e) => {
                                e.preventDefault();

                                e.stopPropagation();

                                void ruleForm.handleSubmit();
                              }}
                            >
                              <ruleForm.Field
                                name="value"
                                validators={{
                                  onSubmit: ({ value }) => {
                                    const entry = value.trim();

                                    if (!entry) return 'Please enter a value.';

                                    const isDomain =
                                      showConfigRules ===
                                      ProjectRuleType.domain;

                                    const isValid = isDomain
                                      ? /^([a-z0-9-]+\.)+[a-z]{2,}$/i.test(
                                          entry
                                        )
                                      : /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
                                          entry
                                        );

                                    if (!isValid) {
                                      return isDomain
                                        ? 'Please enter a valid domain, e.g. example.com (without the @).'
                                        : 'Please enter a valid email address.';
                                    }

                                    return stagedRules.some(
                                      (r) =>
                                        r.type === showConfigRules &&
                                        r.value === entry
                                    )
                                      ? `This ${
                                          isDomain ? 'domain' : 'email'
                                        } has already been added.`
                                      : undefined;
                                  },
                                }}
                                children={({ state, handleChange }) => (
                                  <Input
                                    value={state.value}
                                    error={state.meta.errors.join(', ')}
                                    className="grow"
                                    inputClassname="h-10!"
                                    label={
                                      showConfigRules === ProjectRuleType.domain
                                        ? 'Domains'
                                        : 'Emails'
                                    }
                                    helperText={
                                      showConfigRules === ProjectRuleType.domain
                                        ? `For example as your email is ${
                                            user.email
                                          } the domain would be ${
                                            user.email.split('@')[1]
                                          }. Users that utilise this domain for their email will be able to sign in via their email.`
                                        : 'Users directly added will be able to sign in via their email.'
                                    }
                                    onChange={(e) =>
                                      handleChange(e.target.value)
                                    }
                                    inlineButton={{
                                      button: {
                                        type: 'secondary',
                                        text: 'Add',
                                        IconEnd: PlusIcon,
                                        isSubmit: true,
                                        shrink: true,
                                      },
                                    }}
                                  />
                                )}
                              />
                            </form>
                          </div>
                          <div className="overflow-hidden flex flex-col">
                            <div className="overflow-y-auto overflow-x-hidden space-y-2 pb-5 px-5">
                              {state.value
                                .filter((v) => v.type === showConfigRules)
                                .map((v) => (
                                  <div
                                    key={v.uuid}
                                    className="flex items-center space-x-1 bg-grey-t-900 justify-between border border-grey-500 rounded-xs"
                                  >
                                    <div className="pl-2.5 py-2 space-x-2 flex items-center">
                                      {v.type === ProjectRuleType.domain ? (
                                        <GlobeIcon className="size-4" />
                                      ) : (
                                        <EnvelopeIcon className="size-4" />
                                      )}
                                      <span className="text-sm">{v.value}</span>
                                    </div>
                                    <IconButton
                                      onClick={() =>
                                        handleChange((vals) =>
                                          vals.filter(
                                            (val) => val.uuid !== v.uuid
                                          )
                                        )
                                      }
                                      Icon={XMarkIcon}
                                    />
                                  </div>
                                ))}
                            </div>
                          </div>
                        </div>
                      </div>
                      <ModalFooter
                        submitText="Add"
                        closeText="Close"
                        onClose={() => setShowConfigRules(undefined)}
                        onSubmit={() => setShowConfigRules(undefined)}
                      />
                    </Modal>
                  </div>
                )}
              />
            </div>
            <div
              data-hidden={tab !== 'domain' ? true : undefined}
              className="data-hidden:hidden p-5 space-y-5"
            >
              <Alert
                type="low-priority"
                message={
                  <span className="break-all">
                    {project.state === ProjectState.published
                      ? 'Your knowledge base is available at'
                      : 'Your knowledge base will be available on'}{' '}
                    <a
                      target="_blank"
                      rel="noreferrer noopener"
                      className="underline text-primary-400"
                      href={domainName}
                    >
                      {domainName}
                    </a>
                  </span>
                }
              />
              <hr className="h-px border-none bg-grey-t-800" />
              {!canAddDomain ? (
                <Button
                  button={{
                    text: 'Add domain',
                    type: 'low-priority',
                    size: 'medium',
                    shrink: true,
                    onClick: () => setShowDomainUpgrade(true),
                  }}
                />
              ) : (
                <form.Field
                  name="domains"
                  children={({ state, handleChange }) => (
                    <div className="flex items-start space-x-2">
                      <div className="grow">
                        <ComboxBox
                          label="Custom domain (optional)"
                          OptionIconStart={GlobeIcon}
                          options={domains.indexDomains.items.map((i) => ({
                            label: i.url,
                            value: i.uuid,
                          }))}
                          onSelect={(opt) => {
                            if (
                              state.value.some((o) => o.value === opt.value)
                            ) {
                              client.cache.updateQuery(
                                {
                                  query: GQL_QUERY_INDEX_DOMAINS,
                                  variables: {
                                    input: {
                                      pagination: {
                                        perPage: 15,
                                        page: 1,
                                      },
                                      filters: {
                                        unallocated: true,
                                      },
                                    },
                                  },
                                },
                                (d) =>
                                  d
                                    ? {
                                        ...d,
                                        indexDomains: {
                                          ...d.indexDomains,
                                          items: [
                                            ...d.indexDomains.items,
                                            {
                                              url: opt.label,
                                              uuid: opt.value,
                                              projectUuid: null,
                                              createdAt:
                                                new Date().toISOString(),
                                              cloudflareStatus: 'pending',
                                            },
                                          ],
                                        },
                                      }
                                    : null
                              );

                              handleChange((vals) =>
                                vals.filter((v) => v.value !== opt.value)
                              );
                            } else {
                              handleChange((vals) => [...vals, opt]);

                              client.cache.updateQuery(
                                {
                                  query: GQL_QUERY_INDEX_DOMAINS,
                                  variables: {
                                    input: {
                                      pagination: {
                                        perPage: 15,
                                        page: 1,
                                      },
                                      filters: {
                                        unallocated: true,
                                      },
                                    },
                                  },
                                },
                                (d) =>
                                  d
                                    ? {
                                        ...d,
                                        indexDomains: {
                                          ...d.indexDomains,
                                          items: d.indexDomains.items.filter(
                                            (i) => i.uuid !== opt.value
                                          ),
                                        },
                                      }
                                    : null
                              );
                            }
                          }}
                          selected={state.value}
                        />
                      </div>
                      {/* mt-7 clears the combobox label (20px line + 8px
                          margin) so the button lines up with the input row */}
                      <div className="mt-7 shrink-0">
                        <Button
                          button={{
                            text: 'Add domain',
                            type: 'secondary',
                            size: 'medium',
                            shrink: true,
                            IconEnd: PlusIcon,
                            onClick: () => setShowCreateDomain(true),
                          }}
                        />
                      </div>
                    </div>
                  )}
                />
              )}
            </div>
          </div>
        </div>
        <ModalFooter
          buttonsDisabled={isSaving}
          onClose={onClose}
          asForm
          submitText={isPublishMode ? 'Publish' : 'Save'}
        />
      </form>
      <Modal
        hideTopHr
        open={showUpgrade}
        onClose={() => setShowUpgrade(false)}
        dialogPanelClassname="w-120"
      >
        <BillingLite
          subscriptionType={SubscriptionType.essentials}
          upgradePrompt={PUBLISH_UPGRADE_PROMPT}
          Icon={Cog6ToothIcon}
          modal
          onSuccess={() => {
            setShowUpgrade(false);

            // Subscription now active — publish the draft we saved earlier.
            forcePublishRef.current = true;

            void form.handleSubmit();
          }}
        />
      </Modal>
      <Modal
        hideTopHr
        open={showDomainUpgrade}
        onClose={() => setShowDomainUpgrade(false)}
        dialogPanelClassname="w-120"
      >
        <BillingLite
          subscriptionType={SubscriptionType.essentials}
          upgradePrompt="Unlock custom domains to give branded access to your team."
          Icon={GlobeAltIcon}
          modal
          onSuccess={() => {
            setShowDomainUpgrade(false);

            setShowCreateDomain(true);
          }}
        />
      </Modal>
      <CreateDomainModal
        open={showCreateDomain}
        onClose={() => setShowCreateDomain(false)}
        onCreated={setVerifyDomain}
      />
      <DomainVerificationModal
        domain={verifyDomain}
        onClose={() => setVerifyDomain(undefined)}
      />
    </>
  );
};

export const ProjectSettingsModal = ({
  open,
  onClose,
  onPublished,
  project,
}: Props): ReactElement => {
  const [isLoading, setIsLoading] = useState(false);

  return (
    <Modal
      title="Knowledge base settings"
      loading={isLoading}
      open={open}
      onClose={onClose}
      dialogPanelClassname="min-h-150 w-[860px]"
    >
      <ProjectSettingsInner
        project={project}
        onClose={onClose}
        onPublished={onPublished}
        setLoading={setIsLoading}
      />
    </Modal>
  );
};
